import {
	CART_KEY,
	LAST_ORDER_ID_KEY,
	LAST_ORDER_KEY,
	ORDER_CONTEXT_KEY,
} from "@/constants/storageKeys";
import type {
	CartItem,
	CustomerOrderContext,
	OrderDetails,
} from "@/types/order.type";
import { getAuthSession } from "@/utils/authStorage";

const PENDING_PAYMENT_ORDER_KEY = "pendingPaymentOrder";

const safeParse = <T>(raw: string | null, fallback: T): T => {
	try {
		return raw ? (JSON.parse(raw) as T) : fallback;
	} catch {
		return fallback;
	}
};

export const cartStorage = {
	get(): CartItem[] {
		return safeParse<CartItem[]>(localStorage.getItem(CART_KEY), []);
	},

	set(items: CartItem[]) {
		localStorage.setItem(CART_KEY, JSON.stringify(items));
		window.dispatchEvent(new Event("cart:updated"));
	},

	clear() {
		localStorage.removeItem(CART_KEY);
		window.dispatchEvent(new Event("cart:updated"));
	},

	cartLineKey(item: Pick<CartItem, "lotId" | "purchaseUnitId" | "unitId">) {
		const purchaseUnitId = item.purchaseUnitId ?? item.unitId ?? "";
		return `${item.lotId}:${purchaseUnitId}`;
	},

	add(item: Omit<CartItem, "qty">, qty = 1) {
		const items = this.get();
		const key = this.cartLineKey(item);
		const found = items.find((x) => this.cartLineKey(x) === key);

		if (found) {
			found.qty += qty;
		} else {
			items.push({ ...item, qty });
		}

		this.set(items);
	},

	getTotalQty() {
		return this.get().reduce((sum, item) => sum + item.qty, 0);
	},
};

export const orderContextStorage = {
	get(): CustomerOrderContext {
		return safeParse<CustomerOrderContext>(
			localStorage.getItem(ORDER_CONTEXT_KEY),
			{},
		);
	},

	set(value: CustomerOrderContext) {
		localStorage.setItem(ORDER_CONTEXT_KEY, JSON.stringify(value));
		window.dispatchEvent(new Event("order-context:updated"));
	},

	patch(value: Partial<CustomerOrderContext>) {
		const current = this.get();
		this.set({ ...current, ...value });
	},

	clear() {
		localStorage.removeItem(ORDER_CONTEXT_KEY);
		window.dispatchEvent(new Event("order-context:updated"));
	},

	/**
	 * Đủ ngữ cảnh giao/nhận để xem sản phẩm / dùng giỏ (Home, Cart).
	 *
	 * DELIVERY: chỉ cần map + text; addressId có thể được resolve khi vào Checkout.
	 */
	isContextSufficientForShopping(ctx?: CustomerOrderContext) {
		const value = ctx ?? this.get();

		if (!value.deliveryMethodId) return false;

		if (value.deliveryMethodId === "DELIVERY") {
			return (
				!!value.addressText &&
				typeof value.lat === "number" &&
				typeof value.lng === "number"
			);
		}

		if (value.deliveryMethodId === "PICKUP") {
			return !!(
				value.collectionId ||
				value.collectionPointId ||
				value.pickupPointId
			);
		}

		return false;
	},

	/**
	 * Khớp contract BE khi tạo đơn: DELIVERY bắt buộc addressId; dùng trước thanh toán (Checkout).
	 */
	isReady(ctx?: CustomerOrderContext) {
		const value = ctx ?? this.get();

		if (!value.deliveryMethodId) return false;

		if (value.deliveryMethodId === "DELIVERY") {
			return (
				!!value.addressId &&
				!!value.addressText &&
				typeof value.lat === "number" &&
				typeof value.lng === "number"
			);
		}

		if (value.deliveryMethodId === "PICKUP") {
			return !!(
				value.collectionId ||
				value.collectionPointId ||
				value.pickupPointId
			);
		}

		return false;
	},

	getResolvedCollectionId(ctx?: CustomerOrderContext) {
		const value = ctx ?? this.get();
		return (
			value.collectionId ||
			value.collectionPointId ||
			value.pickupPointId ||
			""
		);
	},

	getResolvedCollectionName(ctx?: CustomerOrderContext) {
		const value = ctx ?? this.get();
		return value.collectionPointName || value.pickupPointName || "";
	},

	getResolvedCollectionAddress(ctx?: CustomerOrderContext) {
		const value = ctx ?? this.get();
		return value.collectionPointAddress || value.pickupPointAddress || "";
	},
};

export const lastOrderStorage = {
	getId() {
		return localStorage.getItem(LAST_ORDER_ID_KEY) ?? "";
	},

	setId(orderId: string) {
		localStorage.setItem(LAST_ORDER_ID_KEY, orderId);
	},

	clearId() {
		localStorage.removeItem(LAST_ORDER_ID_KEY);
	},

	get<T>() {
		return safeParse<T | null>(localStorage.getItem(LAST_ORDER_KEY), null);
	},

	set<T>(value: T) {
		localStorage.setItem(LAST_ORDER_KEY, JSON.stringify(value));
	},

	clear() {
		localStorage.removeItem(LAST_ORDER_KEY);
		localStorage.removeItem(LAST_ORDER_ID_KEY);
	},
};

export const pendingPaymentOrderStorage = {
	get() {
		return safeParse<OrderDetails | null>(
			localStorage.getItem(PENDING_PAYMENT_ORDER_KEY),
			null,
		);
	},

	set(order: OrderDetails) {
		localStorage.setItem(PENDING_PAYMENT_ORDER_KEY, JSON.stringify(order));
	},

	clear() {
		localStorage.removeItem(PENDING_PAYMENT_ORDER_KEY);
	},
};

export const money = (value: number) => `${value.toLocaleString("vi-VN")} đ`;

export const googleMapsUrl = (lat: number, lng: number) =>
	`https://www.google.com/maps?q=${lat},${lng}`;

export const getAuthenticatedUserId = () => {
	const session = getAuthSession();
	const user = session?.user as Record<string, unknown> | undefined;

	if (!user) return "";

	const candidate =
		user.userId ??
		user.id ??
		user.userID ??
		user[
		"http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
		];

	return typeof candidate === "string" ? candidate : "";
};

export const getCheckoutUserId = () => {
	return getAuthenticatedUserId();
};
